import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useCancelReportMutation } from '@/store/api/Person/Person.query'

interface CancelReportDialogProps {
  id: string
  children: ReactNode
}

export const CancelReportDialog = ({
  id,
  children,
}: CancelReportDialogProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [cancelReport, { isLoading }] = useCancelReportMutation()

  const handleConfirm = async () => {
    try {
      await cancelReport({ id }).unwrap()
      toast.success(t('dashboard.reportsScreen.cancelReportSuccess'))
      setOpen(false)
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('dashboard.team.error'))
    }
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent side="right" className="flex flex-col gap-0">
        <SheetHeader className="pb-2">
          <SheetTitle className="text-lg font-medium text-neutralColor">
            {t('dashboard.reportsScreen.cancelReportGeneration')}
          </SheetTitle>
          <SheetDescription className="text-sm text-neutralColor60 text-left">
            {t('dashboard.reportsScreen.thisActionWill')}
          </SheetDescription>
        </SheetHeader>

        <SheetFooter className="mt-auto pt-6 flex flex-row justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setOpen(false)}
            disabled={isLoading}
          >
            {t('dashboard.team.removalCancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleConfirm}
            isLoading={isLoading}
            className="bg-dynamicRed hover:bg-dynamicRed/90"
          >
            {t('dashboard.team.confirm')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
